import { useEffect } from "react";
import { Bookmark } from "lucide-react";
import { useAuth } from "../lib/auth";
import { ensureSavedLoaded, toggleSaved, useIsSaved } from "../lib/saved";
import { useI18n } from "../lib/i18n";

type SaveButtonProps = {
  kind: string;
  refId: string;
  className?: string;
  label?: string;
  hoverLabel?: string;
  iconClassName?: string;
  savedTone?: "red" | "gold";
  imageOverlay?: boolean;
};

/** Bookmark toggle for any listing (item, service, company, person, job, project, rfq).
 *  Icon-only by default; pass `label` to render a full button (e.g. detail-page CTA). */
export function SaveButton({ kind, refId, className = "", label, hoverLabel, iconClassName = "w-4 h-4", savedTone = "gold", imageOverlay = false }: SaveButtonProps) {
  const { t } = useI18n();
  const displayLabel = label === "Save" ? t("discovery.save") : label;
  const displayHoverLabel = hoverLabel === "Save" ? t("discovery.save") : hoverLabel;
  const { user, canStartMarketplaceActivity } = useAuth();
  const companyBlocked = !canStartMarketplaceActivity;
  const saved = useIsSaved(kind, refId);
  const savedTextClass = savedTone === "gold" ? "text-brand-gold" : "text-brand-red";
  const savedFillClass = savedTone === "gold" ? "fill-brand-gold" : "fill-brand-red";
  useEffect(() => { if (user) ensureSavedLoaded(); }, [user]);
  if (!user) return null;
  if (label) {
    return (
      <button type="button" disabled={companyBlocked} title={companyBlocked ? "Business verification required" : undefined} onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleSaved(kind, refId); }}
        className={`btn-outline ${saved ? savedTextClass : ""} ${className}`}>
        <Bookmark className={`${iconClassName} ${saved ? savedFillClass : ""}`} />{saved ? t("discovery.saved") : displayLabel}
      </button>
    );
  }
  if (hoverLabel) {
    const saveLabel = saved ? t("discovery.saved") : displayHoverLabel;
    return (
      <button
        type="button"
        disabled={companyBlocked}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleSaved(kind, refId); }}
        title={saved ? t("discovery.savedHint") : displayHoverLabel}
        aria-label={saved ? t("discovery.removeSaved") : displayHoverLabel}
        className={`group inline-flex h-10 w-10 items-center justify-center gap-0 overflow-hidden rounded-lg border border-line bg-white px-0 text-navy transition-all hover:w-auto hover:gap-2 hover:px-3 focus-visible:w-auto focus-visible:gap-2 focus-visible:px-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy focus-visible:ring-offset-2 ${saved ? savedTextClass : ""} ${className}`}
      >
        <Bookmark className={`h-5 w-5 shrink-0 ${saved ? savedFillClass : ""}`} />
        <span className="max-w-0 overflow-hidden whitespace-nowrap text-sm font-semibold opacity-0 transition-[max-width,opacity] group-hover:max-w-20 group-hover:opacity-100 group-focus-visible:max-w-20 group-focus-visible:opacity-100">{saveLabel}</span>
      </button>
    );
  }
  return (
    <button
      type="button"
      disabled={companyBlocked}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleSaved(kind, refId); }}
      title={saved ? t("discovery.savedHint") : t("discovery.save")}
      aria-label={saved ? t("discovery.removeSaved") : t("discovery.save")}
      className={`${imageOverlay ? "grid place-items-center bg-transparent p-0 shadow-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white" : "grid place-items-center rounded-none bg-transparent p-0 transition-colors"} ${saved ? `${className} ${savedTextClass}` : `${imageOverlay ? "text-white hover:text-white/80" : "text-mut hover:text-navy"} ${className}`}`}
    >
      <Bookmark className={`${iconClassName} ${saved ? savedFillClass : imageOverlay ? "fill-white" : ""}`} />
    </button>
  );
}
